import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import merge from "lodash/merge";

import { hydrate } from "@/lib";
import type { Profile, RegisterAccount, ResponsePayload } from "@/types";
import {
  createUnverifiedUser,
  fetchUser,
  loginRegisteredUser,
  validateUser,
  updatePassword,
  updateProfile,
  signOutUser
} from "./thunk";

export interface UserState {
  error: string | null;
  pageData: {
    toolId: string;
    toolType: string;
    siteName: string;
  };
  status: "idle" | "fetching" | "saving";
  registeredUser: {
    login?: string;
    password?: string;
    isLoggedIn?: boolean;
    profile?: Partial<Profile>;
    accountDetails: Partial<RegisterAccount>;
    hasPassWordChanged: boolean;
  };
  unverifiedUser: { email?: string; login?: string; token?: string };
  type?: string;
}

const initialState: UserState = {
  error: null,
  status: "idle",
  pageData: {
    toolId: null,
    toolType: null,
    siteName: null
  },
  registeredUser: {
    login: "",
    password: "",
    isLoggedIn: false,
    profile: {},
    accountDetails: {},
    hasPassWordChanged: false
  },
  unverifiedUser: { email: "", login: "", token: null }
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateRegisteredUser: (state, action) => {
      state.registeredUser = merge(state.registeredUser, action.payload);
    },
    resetRegisterededUser: (state) => {
      state.error = null;
      state.registeredUser = initialState.registeredUser;
    },
    updateUnverifiedUser: (state, action) => {
      state.unverifiedUser = merge(state.unverifiedUser, action.payload);
    },
    resetUnverifiedUser: (state) => {
      state.error = null;
      state.unverifiedUser = initialState.unverifiedUser;
    },
    setPageData: (state, action) => {
      state.pageData = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setHasPassWordChanged: (state, action) => {
      state.registeredUser.hasPassWordChanged = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => ({
        ...state,
        ...action.payload[userSlice.name]
      }))
      .addCase(createUnverifiedUser.pending, (state) => {
        state.error = null;
        state.status = "saving";
      })
      .addCase(createUnverifiedUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.unverifiedUser = { ...state.unverifiedUser, ...action.payload };
      })
      .addCase(createUnverifiedUser.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(loginRegisteredUser.pending, (state) => {
        state.error = null;
        state.status = "fetching";
      })
      .addCase(loginRegisteredUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.registeredUser = { ...state.registeredUser, isLoggedIn: true };
      })
      .addCase(loginRegisteredUser.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(fetchUser.pending, (state) => {
        state.error = null;
        state.status = "fetching";
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.status = "idle";
        const { type = null } = action?.payload || {};
        state.type = type;
        if (type === "User") {
          state.registeredUser.profile = action.payload;
        }
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(validateUser.pending, (state) => {
        state.error = null;
        state.status = "fetching";
      })
      .addCase(validateUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.registeredUser.accountDetails = action.payload;
      })
      .addCase(validateUser.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(updatePassword.pending, (state) => {
        state.error = null;
        state.status = "saving";
        state.registeredUser.hasPassWordChanged = false;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.status = "idle";
        state.registeredUser.hasPassWordChanged = true;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        const { data } = action.payload as ResponsePayload;

        state.status = "idle";

        state.error = (data as any)?.errors[0]?.code;
      })
      .addCase(updateProfile.pending, (state) => {
        state.error = null;
        state.status = "saving";
      })
      .addCase(updateProfile.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(signOutUser.pending, (state) => {
        state.error = null;
        state.status = "fetching";
      })
      .addCase(signOutUser.fulfilled, (state) => {
        state.status = "idle";
        state.registeredUser = initialState.registeredUser;
        state.unverifiedUser = initialState.unverifiedUser;
      })
      .addCase(signOutUser.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.error = statusText;
      });
  }
});

export const {
  setError,
  setPageData,
  resetUnverifiedUser,
  updateRegisteredUser,
  resetRegisterededUser,
  updateUnverifiedUser,
  setHasPassWordChanged
} = userSlice.actions;
